import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Paper, Grid, TextField, Button,
  Alert, LinearProgress
} from '@mui/material';
import {
  Save as SaveIcon,
  Settings as SettingsIcon,
  Sync as SyncIcon,
} from '@mui/icons-material';
import { authGet, authPut, authPost } from '../../services/apiService';
import ReliabilitySubCategoryAdmin from './ReliabilitySubCategoryAdmin';
import ServiceManagementAdmin from './ServiceManagementAdmin';

const darkGreenColor = '#1D4434';

const PILLARS = [
  { field: 'corePlatformEpicId', label: 'Core Platform & Runtime Health', helper: 'Epic ID or ADO URL for Core Platform & Runtime Health pillar' },
  { field: 'thirdPartyEpicId', label: 'Third-Party Integration Reliability', helper: 'Epic ID or ADO URL for Third-Party Integration Reliability pillar' },
  { field: 'observabilityEpicId', label: 'Safe Delivery', helper: 'Epic ID or ADO URL for Safe Delivery pillar' },
];

const ReliabilityAdmin = () => {
  const [loadingConfig, setLoadingConfig] = useState(true);
  const [savingConfig, setSavingConfig] = useState(false);
  const [configSuccess, setConfigSuccess] = useState(null);
  const [configError, setConfigError] = useState(null);

  const [syncingTags, setSyncingTags] = useState(false);
  const [syncResult, setSyncResult] = useState(null);
  const [syncError, setSyncError] = useState(null);

  const [epicIds, setEpicIds] = useState({
    corePlatformEpicId: '',
    thirdPartyEpicId: '',
    observabilityEpicId: '',
  });

  useEffect(() => { loadConfig(); }, []);

  const loadConfig = async () => {
    try {
      setLoadingConfig(true);
      const res = await authGet('/api/reliability/config');
      if (res.success) {
        setEpicIds({
          corePlatformEpicId: res.config.corePlatformEpicId || '',
          thirdPartyEpicId: res.config.thirdPartyEpicId || '',
          observabilityEpicId: res.config.observabilityEpicId || '',
        });
      } else {
        setConfigError('Failed to load configuration');
      }
    } catch (e) {
      setConfigError(e.response?.data?.message || 'Failed to load configuration');
    } finally {
      setLoadingConfig(false);
    }
  };

  const handleSave = async () => {
    try {
      setSavingConfig(true);
      setConfigError(null);
      setConfigSuccess(null);
      const res = await authPut('/api/reliability/config', epicIds);
      if (res.success) {
        setConfigSuccess('Configuration saved successfully');
        setTimeout(() => setConfigSuccess(null), 5000);
      } else {
        setConfigError(res.message || 'Failed to save configuration');
      }
    } catch (e) {
      setConfigError(e.response?.data?.message || 'Failed to save configuration');
    } finally {
      setSavingConfig(false);
    }
  };

  const handleSyncTags = async () => {
    try {
      setSyncingTags(true);
      setSyncError(null);
      setSyncResult(null);
      const res = await authPost('/api/reliability/sync/feature-tags', {});
      if (res.success) {
        setSyncResult(res.message || 'Tags synced successfully');
        setTimeout(() => setSyncResult(null), 8000);
      } else {
        setSyncError(res.message || 'Failed to sync tags');
      }
    } catch (e) {
      setSyncError(e.response?.data?.message || 'Failed to sync tags from ADO');
    } finally {
      setSyncingTags(false);
    }
  };

  const disabled = loadingConfig || savingConfig;

  return (
    <Box p={3}>
      <Box display="flex" alignItems="center" mb={3}>
        <SettingsIcon sx={{ mr: 1, color: darkGreenColor }} />
        <Typography variant="h5" component="h1">
          Reliability & Scale Program — Configuration
        </Typography>
      </Box>

      {loadingConfig ? (
        <LinearProgress sx={{ mb: 2 }} />
      ) : (
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                Azure DevOps Epic Configuration
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Set the Azure DevOps Epic ID (or full ADO URL) for each pillar. Work items are kept up to date automatically via the ADO webhook. The dashboard and Product Engineering tab compute progress from the EpicHierarchy populated by that webhook.
              </Typography>

              <Grid container spacing={2}>
                {PILLARS.map(({ field, label, helper }) => (
                  <Grid item xs={12} md={6} key={field}>
                    <TextField
                      fullWidth
                      label={label}
                      value={epicIds[field]}
                      onChange={e => setEpicIds(prev => ({ ...prev, [field]: e.target.value }))}
                      placeholder="Enter Epic ID or ADO URL"
                      helperText={helper}
                      disabled={disabled}
                      size="small"
                    />
                  </Grid>
                ))}

                <Grid item xs={12}>
                  <Box display="flex" gap={2} flexWrap="wrap" alignItems="center">
                    <Button
                      variant="contained"
                      onClick={handleSave}
                      disabled={disabled}
                      startIcon={<SaveIcon />}
                      sx={{ bgcolor: darkGreenColor, '&:hover': { bgcolor: '#0f2e1f' } }}
                    >
                      {savingConfig ? 'Saving…' : 'Save Configuration'}
                    </Button>
                    <Button
                      variant="outlined"
                      onClick={handleSyncTags}
                      disabled={syncingTags || loadingConfig}
                      startIcon={<SyncIcon />}
                      sx={{ borderColor: darkGreenColor, color: darkGreenColor, '&:hover': { bgcolor: `${darkGreenColor}0d` } }}
                    >
                      {syncingTags ? 'Syncing tags…' : 'Sync Tags from ADO'}
                    </Button>
                  </Box>
                </Grid>
              </Grid>

              {configSuccess && <Alert severity="success" sx={{ mt: 2 }}>{configSuccess}</Alert>}
              {configError && <Alert severity="error" sx={{ mt: 2 }}>{configError}</Alert>}
              {syncResult && <Alert severity="success" sx={{ mt: 2 }}>{syncResult} — reload Product Engineering and Delivery tabs to see updated data.</Alert>}
              {syncError && <Alert severity="error" sx={{ mt: 2 }}>{syncError}</Alert>}

              <Paper variant="outlined" sx={{ p: 2, mt: 3, bgcolor: 'info.50' }}>
                <Typography variant="subtitle2" gutterBottom fontWeight="bold">About the Epics:</Typography>
                <Box component="ul" sx={{ pl: 3, m: 0 }}>
                  {PILLARS.map(({ label }) => (
                    <li key={label}><Typography variant="body2">{label}</Typography></li>
                  ))}
                </Box>
                <Typography variant="body2" sx={{ mt: 1 }}>
                  Progress, features, and story points are computed from the Features and Backlog Items under each Epic.
                  Tags on features determine their initiative grouping (Product Engineering tab) and quarterly delivery schedule (Delivery tab — Q3-2026 / Q4-2026 tags).
                  Use <strong>Sync Tags from ADO</strong> if a feature tag was added in ADO but isn't reflected yet. Fix versions are kept up to date automatically via the ADO webhook.
                </Typography>
              </Paper>
            </Paper>
          </Grid>
        </Grid>
      )}

      {/* Sub-category management */}
      <Box mt={4}>
        <ReliabilitySubCategoryAdmin />
      </Box>

      {/* Service Management configuration */}
      <Box mt={4}>
        <ServiceManagementAdmin />
      </Box>
    </Box>
  );
};

export default ReliabilityAdmin;
